/**
 * Demo scenarios that start a **process** (17h) instead of a single task: the scenario user starts the process; a
 * runner on the platform feeds the outside world (the mock systems: supplier portal, bank, clock) through an ordered
 * side-event plan as the case advances, and leaves every human gate and work item to the people in the workbench —
 * or, with `auto`, decides gates and completes work items as the configured users.
 *
 * Everything domain-specific (process name, mock route names, record ids) is scenario data shipped by a pack
 * (`seeds/demo/scenarios.json`); the runner only knows the generic platform API (cases, approval requests, work
 * items) and the mock-systems route catalog.
 *
 * Values in `input`, side-event steps and work-item outputs are **templates** (`DemoTemplate`): a string that is
 * exactly one `{{expr}}` keeps the type of its value, other strings interpolate. Expressions: `token` (run token),
 * `n` (run number 1–999), `num:<arithmetic over n>`, `date:<±days>`, `link:<relation>[:<role>[:<nth>]]` (ref id of a
 * case link; an unresolved link holds the event back), `with.<name>.<path>` (a step lookup), `ev.<eventId>.<path>`
 * (output of an earlier side event), `input.<path>` / `item.<path>` (work-item input, `$map` item). Objects
 * `{ "$from": path, "$set": {…} }`, `{ "$map": path, "$to": template }` and
 * `{ "$when": { "path": p, "equals": v }, "$then": t, "$else": t }` build values from those.
 */
import { z } from 'zod';
import { IsoDateTime } from './common.ts';

/** Template value (see the module doc); validated when the runner resolves it. */
export const DemoTemplate = z.unknown();

/**
 * When a side event may fire. Every given condition must hold (AND); with none, it fires right after the previous
 * event. `nth` counts matching items (activities succeeded, gate requests approved, links, completed work items).
 */
export const DemoSideEventTrigger = z
  .object({
    /** Activity id (or activation id `<activityId>#<n>`) that has succeeded. */
    activity: z.string().min(1).optional(),
    /** Gate id with an approved request. */
    gate: z.string().min(1).optional(),
    /** Case link of this relation (and role). */
    link: z.object({ relation: z.string().min(1), role: z.string().min(1).optional() }).optional(),
    /** Completed work item of this activity id. */
    workItem: z.string().min(1).optional(),
    /** Mock-systems state: a read route whose list (`path`, default `items`) has at least `min` matching items. */
    mock: z
      .object({
        call: z.string().min(1),
        params: z.record(z.string(), DemoTemplate).optional(),
        query: z.record(z.string(), DemoTemplate).optional(),
        path: z.string().min(1).default('items'),
        /** Field → `{ equals }` or `{ startsWith }` (every listed field must match). */
        where: z
          .record(z.string(), z.object({ equals: z.unknown().optional(), startsWith: z.string().optional() }))
          .optional(),
        min: z.number().int().positive().default(1),
      })
      .optional(),
    nth: z.number().int().positive().default(1),
    /** Wait this long once the other conditions hold (alone: after the previous event). */
    delayMs: z.number().int().nonnegative().max(3_600_000).optional(),
  })
  .meta({ id: 'DemoSideEventTrigger' });
export type DemoSideEventTrigger = z.infer<typeof DemoSideEventTrigger>;

/** One mock-systems route call (route name from the mock route catalog, e.g. `p2pSubmitPortalInvoice`). */
export const DemoMockCall = z
  .object({
    call: z.string().min(1),
    params: z.record(z.string(), DemoTemplate).optional(),
    query: z.record(z.string(), DemoTemplate).optional(),
    body: DemoTemplate.optional(),
    /** Idempotency key (template); default `demo:<runId>:<eventId>:<step>`. */
    idempotencyKey: z.string().min(1).optional(),
    /** Name under which the response is kept for later steps (`with.<as>`) and events (`ev.<eventId>.<as>`). */
    as: z
      .string()
      .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/)
      .optional(),
  })
  .meta({ id: 'DemoMockCall' });
export type DemoMockCall = z.infer<typeof DemoMockCall>;

/** One entry of the ordered side-event plan: the outside world acting on the mock systems. */
export const DemoSideEvent = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/),
    /** Progress line, e.g. "Supplier acknowledges the PO". */
    label: z.string().min(1).max(200),
    after: DemoSideEventTrigger.prefault({}),
    /** Reads that run first (their results are `with.<as>`), e.g. the PO revision an acknowledgment needs. */
    with: z.array(DemoMockCall).default([]),
    steps: z.array(DemoMockCall).min(1),
    /** Deliver due webhooks after the steps (default true). */
    pump: z.boolean().default(true),
  })
  .meta({ id: 'DemoSideEvent' });
export type DemoSideEvent = z.infer<typeof DemoSideEvent>;

/** An `auto` decision other than approve, for the nth request (1-based, creation order) of a gate. */
export const DemoAutoDecision = z
  .object({
    gate: z.string().min(1),
    nth: z.number().int().positive().default(1),
    decision: z.enum(['return', 'reject']),
    /** Stage that returns/rejects (default: the first open stage). */
    stage: z.string().min(1).optional(),
    rationale: z.string().min(1).max(500).optional(),
  })
  .meta({ id: 'DemoAutoDecision' });
export type DemoAutoDecision = z.infer<typeof DemoAutoDecision>;

/** Output `auto` submits for the nth work item (1-based) of an activity; default `{ "$from": "input.prefill" }`. */
export const DemoWorkItemOutput = z
  .object({
    activity: z.string().min(1),
    nth: z.number().int().positive().optional(),
    output: DemoTemplate,
  })
  .meta({ id: 'DemoWorkItemOutput' });
export type DemoWorkItemOutput = z.infer<typeof DemoWorkItemOutput>;

/**
 * One fact of the presenter's facts card (31): the `auto` runner answers a clarification question with the `answer`
 * of the best-matching fact. `match` holds case-insensitive keywords or phrases; each one matches where a word of the
 * question starts with it (`deduction` matches "deductionsDue", `cost centre` matches "Which cost centre…"; a keyword
 * that starts with a sign, such as `@0.00`, matches anywhere).
 * `mode: "any"` (default): the fact applies when at least one keyword matches; `"all"`: only when every keyword
 * matches. The score is the number of matching keywords; the highest score wins, a tie goes to the first fact listed.
 */
export const DemoAutoFact = z
  .object({
    /** Short name logged in the run lines (default: the first keyword). */
    id: z.string().min(1).max(60).optional(),
    match: z.array(z.string().trim().min(1)).min(1),
    mode: z.enum(['any', 'all']).default('any'),
    /** Answer template (same `{{expr}}` syntax as `clarify`). */
    answer: z.string().min(1).max(4000),
  })
  .meta({ id: 'DemoAutoFact' });
export type DemoAutoFact = z.infer<typeof DemoAutoFact>;

/** Unattended run: who decides gates and completes work items (role → username, or usernames for a quorum). */
export const DemoAutoConfig = z
  .object({
    decide: z.boolean().default(true),
    workItems: z.boolean().default(true),
    /** Pack role → demo username(s); also used to tell who a waiting gate or work item is waiting for. */
    users: z.record(z.string().min(1), z.union([z.string().min(1), z.array(z.string().min(1)).min(1)])),
    decisions: z.array(DemoAutoDecision).default([]),
    workItemOutputs: z.array(DemoWorkItemOutput).default([]),
    /**
     * Answer (template) to every question of an agent task that asks for clarification (live models may); the task
     * requester (the scenario user) answers. Default: a generic "use the case facts" answer.
     */
    clarify: z.string().min(1).max(4000).optional(),
    /**
     * 31: per-question answers. Each clarification question gets the answer of its best-matching fact
     * (`DemoAutoFact`), else `clarifyFallback`. Without facts every question gets `clarify` as before.
     */
    facts: z.array(DemoAutoFact).default([]),
    /** Answer (template) to a question no fact matches; default `clarify`, else the generic answer. */
    clarifyFallback: z.string().min(1).max(4000).optional(),
  })
  .meta({ id: 'DemoAutoConfig' });
export type DemoAutoConfig = z.infer<typeof DemoAutoConfig>;

/**
 * The mock systems a process scenario drives. `config` names a module config block (`modules.config.<config>`)
 * holding `mockSystems.baseUrl` and `inbound.secretRef`; explicit fields win. The runner registers the webhook
 * subscription of `webhook.domain` to this platform's `webhook.path`, signed with that secret.
 */
export const DemoMockBinding = z
  .object({
    config: z.string().min(1).optional(),
    baseUrl: z.string().min(1).optional(),
    secretRef: z.string().min(1).optional(),
    webhook: z.object({ domain: z.string().min(1), path: z.string().regex(/^\//) }).optional(),
    /** Reads that must succeed before the start (seed records the scenario needs). */
    requires: z.array(DemoMockCall).default([]),
  })
  .meta({ id: 'DemoMockBinding' });
export type DemoMockBinding = z.infer<typeof DemoMockBinding>;

export const DemoRunState = z
  .enum(['starting', 'running', 'waiting', 'succeeded', 'failed', 'stopped'])
  .meta({ id: 'DemoRunState' });
export type DemoRunState = z.infer<typeof DemoRunState>;

/** One progress line of a run. */
export const DemoRunLine = z
  .object({
    seq: z.number().int().nonnegative(),
    at: IsoDateTime,
    kind: z.enum(['info', 'activity', 'gate', 'work-item', 'event', 'decision', 'link', 'error']),
    text: z.string(),
  })
  .meta({ id: 'DemoRunLine' });
export type DemoRunLine = z.infer<typeof DemoRunLine>;

/** Something on the case waiting for a person. */
export const DemoWaiting = z
  .object({
    /** `task`: an agent task of the case waits for clarification answers from its requester. */
    kind: z.enum(['gate', 'work-item', 'task']),
    /** Gate id / activation id. */
    ref: z.string(),
    title: z.string(),
    /** Stage (gates) or owner role (work items) and the users who can act. */
    stage: z.string().optional(),
    role: z.string(),
    users: z.array(z.string()),
    /** Approval request id / work item id / task id. */
    id: z.string(),
  })
  .meta({ id: 'DemoWaiting' });
export type DemoWaiting = z.infer<typeof DemoWaiting>;

/** A process scenario run (`GET /demo/runs/{runId}`); runs live in the API process memory. */
export const DemoRunStatus = z
  .object({
    runId: z.string(),
    scenarioId: z.string(),
    title: z.string(),
    auto: z.boolean(),
    user: z.string().optional(),
    token: z.string(),
    state: DemoRunState,
    caseId: z.string().optional(),
    processInstanceId: z.string().optional(),
    /** Workbench path of the case (`/cases/<caseId>`). */
    caseUrl: z.string().optional(),
    processState: z.string().optional(),
    sideEvents: z.object({ done: z.number().int(), total: z.number().int(), next: z.string().optional() }),
    waiting: z.array(DemoWaiting).default([]),
    lines: z.array(DemoRunLine),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.optional(),
    error: z.string().optional(),
  })
  .meta({ id: 'DemoRunStatus' });
export type DemoRunStatus = z.infer<typeof DemoRunStatus>;
